// src/components/theme/elements/CanvasArea.js

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Stage, Layer, Group, Line as KonvaLine, Rect } from 'react-konva';

const CanvasArea = ({
  elements,
  stageSize,
  scale,
  showGrid,
  gridColor,
  gridLineWidth,
  gridOpacity,
  gridSize,
  renderElement,
  onDrop,
  onDragOver,
  stageRef,
  snapToGridEnabled, // New prop if needed
  backgroundColor,   // New prop for background color
}) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (stageRef && stageRef.current) {
      console.log('Stage ref assigned:', stageRef.current);
    } else {
      console.log('Stage ref not assigned.');
    }
  }, [stageRef]);

  /**
   * Handler for mouse movement over the Stage.
   * Updates the cursorPosition state with the current cursor coordinates.
   * @param {object} e - The Konva event object.
   */
  const handleMouseMove = (e) => {
    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    if (pointerPosition) {
      // Convert to canvas coordinates by dividing by the scale
      const canvasX = Math.round(pointerPosition.x / scale);
      const canvasY = Math.round(pointerPosition.y / scale);
      setCursorPosition({ x: canvasX, y: canvasY });
    }
  };

  /**
   * Handler for mouse leaving the Stage.
   * Resets the cursorPosition state.
   */
  const handleMouseLeave = () => {
    setCursorPosition({ x: 0, y: 0 });
  };

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
        // border: '1px solid black',
        padding: '5px', // Add padding to ensure border visibility
        boxSizing: 'border-box', // Ensure padding doesn't affect overall size
        display: 'flex',
        flexDirection: 'column', // Arrange Stage and cursor info vertically
        alignItems: 'center',    // Center the Stage horizontally
        overflow: 'hidden',      // Prevent scroll
      }}
      onDrop={onDrop}         // Assigning the onDrop handler
      onDragOver={onDragOver} // Assigning the onDragOver handler
    >
      <Stage
        ref={stageRef} // Assign stageRef to Stage
        width={stageSize.width * scale}
        height={stageSize.height * scale}
        scale={{ x: scale, y: scale }}
        onMouseMove={handleMouseMove}   // Track mouse movement
        onMouseLeave={handleMouseLeave} // Handle mouse leaving the Stage
        style={{ backgroundColor: backgroundColor }} // Set background color
      >
        <Layer>
          {/* Background Rectangle */}
          <Rect
            x={0}
            y={0}
            width={stageSize.width}
            height={stageSize.height}
            fill={backgroundColor}
          />

          {/* Render Grid */}
          {showGrid && (
            <Group>
              {/* Vertical Lines */}
              {Array.from({ length: Math.ceil(stageSize.width / gridSize) + 1 }).map((_, i) => (
                <KonvaLine
                  key={`v-${i}`}
                  points={[i * gridSize, 0, i * gridSize, stageSize.height]}
                  stroke={gridColor}
                  strokeWidth={gridLineWidth}
                  opacity={gridOpacity}
                />
              ))}
              {/* Horizontal Lines */}
              {Array.from({ length: Math.ceil(stageSize.height / gridSize) + 1 }).map((_, i) => (
                <KonvaLine
                  key={`h-${i}`}
                  points={[0, i * gridSize, stageSize.width, i * gridSize]}
                  stroke={gridColor}
                  strokeWidth={gridLineWidth}
                  opacity={gridOpacity}
                />
              ))}
            </Group>
          )}

          {/* Render Elements */}
          {elements.map((el) => renderElement(el))}
        </Layer>
      </Stage>

      {/* Cursor Position Display */}
      <div
        style={{
          marginTop: '10px',
          padding: '5px 10px',
          color: 'var(--color-foreground)',
          borderRadius: '4px',
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
        }}
        aria-live="polite" // Announce updates for accessibility
      >
        <strong>Cursor Position:</strong> X = {cursorPosition.x}, Y = {cursorPosition.y}
      </div>
    </div>
  );
};

CanvasArea.propTypes = {
  elements: PropTypes.array.isRequired,
  stageSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  scale: PropTypes.number.isRequired,
  showGrid: PropTypes.bool.isRequired,
  gridColor: PropTypes.string.isRequired,
  gridLineWidth: PropTypes.number.isRequired,
  gridOpacity: PropTypes.number.isRequired,
  gridSize: PropTypes.number.isRequired,
  renderElement: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,      // PropType for onDrop
  onDragOver: PropTypes.func.isRequired,  // PropType for onDragOver
  stageRef: PropTypes.object.isRequired,  // PropType for stageRef
  snapToGridEnabled: PropTypes.bool,       // Optional PropType
  backgroundColor: PropTypes.string.isRequired, // New PropType for backgroundColor
};

export default CanvasArea;
